import type { ArbitrageOpportunity } from '../types/opportunity';

export interface OpportunityLifecycleEvent {
  type: 'INSERT' | 'UPDATE' | 'DELETE';
  opportunity?: ArbitrageOpportunity;
  key: string;
  timestamp: number;
}

export interface FilterCriteria {
  minProfit?: number;
  exchanges?: number[];
  pairs?: string[];
}

export class OpportunityCache {
  private opportunities: Map<string, ArbitrageOpportunity>;
  private lifecycleCallbacks: Set<(event: OpportunityLifecycleEvent) => void>;
  private cleanupInterval: ReturnType<typeof setInterval> | null = null;
  
  // Pre-filtered list for UI display
  private filteredList: ArbitrageOpportunity[] = [];
  private filterCriteria: FilterCriteria = {};
  private needsFilterUpdate = false;
  private isUpdating = false;
  private lastFilterUpdate = 0;
  private readonly MIN_UPDATE_INTERVAL = 100; // Min 100ms between filter updates
  
  constructor() {
    this.opportunities = new Map();
    this.lifecycleCallbacks = new Set();
    // NO CLEANUP NEEDED: Backend sends delete signals (empty levels) when opportunities expire
    // this.startCleanup();
    console.log('[Cache] No cleanup needed - backend handles lifecycle');
  }
  
  has(key: string): boolean {
    return this.opportunities.has(key);
  }
  
  get(key: string): ArbitrageOpportunity | undefined {
    return this.opportunities.get(key);
  }
  
  getAll(): ArbitrageOpportunity[] {
    return Array.from(this.opportunities.values());
  }
  
  /**
   * Generate cache key from opportunity parameters
   * Format matches Rust: [buy_exchange_enum][buy_quote_initial][base][sell_quote_initial][sell_exchange_enum]
   * Example: "3UBTCK0" for Binance(3)→USDT/BTC/KRW→Upbit(0)
   */
  makeCacheKey(opportunity: ArbitrageOpportunity): string {
    return `${opportunity.buy_exchange}${opportunity.buy_quote}${opportunity.base}${opportunity.sell_quote}${opportunity.sell_exchange}`;
  }
  
  insert(opportunity: ArbitrageOpportunity): void {
    const key = this.makeCacheKey(opportunity);
    this.opportunities.set(key, opportunity);
    // DON'T mark for filter update on every insert - too expensive at 700 msg/s
    this.notifyLifecycle({ 
      type: 'INSERT', 
      opportunity, 
      key, 
      timestamp: Date.now() 
    });
  }
  
  update(opportunity: ArbitrageOpportunity): void {
    const key = this.makeCacheKey(opportunity);
    const existing = this.opportunities.get(key);
    
    // Always update, even if timestamps are the same (backend might send same timestamp)
    // This ensures we don't miss updates due to timestamp precision issues
    if (!existing || existing.updated_at <= opportunity.updated_at) {
      this.opportunities.set(key, opportunity);
      // DON'T mark for filter update on every update - too expensive at 700 msg/s
      this.notifyLifecycle({ 
        type: 'UPDATE', 
        opportunity, 
        key, 
        timestamp: Date.now() 
      });
    }
  }
  
  delete(key: string): void {
    const opportunity = this.opportunities.get(key);
    if (opportunity) {
      this.opportunities.delete(key);
      // DON'T mark for filter update on every delete - too expensive at 700 msg/s
      this.notifyLifecycle({ 
        type: 'DELETE', 
        opportunity, 
        key, 
        timestamp: Date.now() 
      });
    }
  }
  
  processOpportunity(opportunity: ArbitrageOpportunity): void {
    const key = this.makeCacheKey(opportunity);
    
    // Check for empty levels (indicates deletion from backend)
    if (opportunity.buy_levels.length === 0 || opportunity.sell_levels.length === 0) {
      this.delete(key);
      return;
    }
    
    const existing = this.opportunities.get(key);
    if (!existing) {
      this.insert(opportunity);
    } else {
      this.update(opportunity);
    }
    
    // Update filtered list if needed (done lazily on next read)
    // The needsFilterUpdate flag is already set by insert/update/delete
    
    // Log cache size periodically (every 100th opportunity)
    if (this.opportunities.size % 100 === 0) {
      console.log('[Cache] Current cache size:', this.opportunities.size, 'opportunities');
    }
  }
  
  /**
   * Clear opportunities for a specific exchange
   */
  clearExchange(exchange: number): void {
    const keysToDelete: string[] = [];
    this.opportunities.forEach((opp, key) => {
      if (opp.buy_exchange === exchange || opp.sell_exchange === exchange) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.delete(key));
  }
  
  onLifecycle(callback: (event: OpportunityLifecycleEvent) => void): () => void {
    this.lifecycleCallbacks.add(callback);
    return () => this.lifecycleCallbacks.delete(callback);
  }
  
  private notifyLifecycle(event: OpportunityLifecycleEvent): void {
    this.lifecycleCallbacks.forEach(callback => {
      try {
        callback(event);
      } catch (error) {
        console.error('[Cache] Lifecycle callback error:', error);
        // Remove the broken callback to prevent further issues
        this.lifecycleCallbacks.delete(callback);
      }
    });
  }
  
  // Cleanup is disabled - backend handles lifecycle through empty price levels
  /* 
  private startCleanup(): void {
    // Not needed - backend sends delete signals
  }
  */
  
  clear(): void {
    // Delete each opportunity to trigger lifecycle events
    const keys = Array.from(this.opportunities.keys());
    keys.forEach(key => this.delete(key));
    
    // Clear the filtered list as well
    this.filteredList = [];
    this.needsFilterUpdate = false;
  }
  
  destroy(): void {
    if (this.cleanupInterval !== null) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.opportunities.clear();
    this.lifecycleCallbacks.clear();
    
    // Clear the filtered list
    this.filteredList = [];
    this.needsFilterUpdate = false;
  }
  
  get size(): number {
    return this.opportunities.size;
  }
  
  // Set filter criteria and mark for update
  setFilter(criteria: FilterCriteria): void {
    this.filterCriteria = criteria;
    this.needsFilterUpdate = true;
    this.updateFilteredList();
    
    // Log filter changes for debugging
    console.log('[Cache] Filter updated:', {
      minProfit: criteria.minProfit,
      exchanges: criteria.exchanges,
      pairs: criteria.pairs,
      resultCount: this.filteredList.length,
      totalCount: this.opportunities.size
    });
  }
  
  // Reset filters to show all opportunities
  resetFilter(): void {
    this.filterCriteria = {};
    this.needsFilterUpdate = true;
    this.updateFilteredList();
    
    console.log('[Cache] Filter reset - showing all opportunities:', {
      count: this.filteredList.length
    });
  }
  
  // Get current filter criteria
  getFilter(): FilterCriteria {
    return { ...this.filterCriteria };
  }
  
  // Update the filtered list based on current criteria
  private updateFilteredList(): void {
    try {
      const startTime = performance.now();
      
      // Always recalculate when called (setFilter/resetFilter forces update)
      let filtered = Array.from(this.opportunities.values());
      
      // Apply filters only if criteria exist
      const hasFilters = this.filterCriteria.minProfit !== undefined ||
                        (this.filterCriteria.exchanges && this.filterCriteria.exchanges.length > 0) ||
                        (this.filterCriteria.pairs && this.filterCriteria.pairs.length > 0);
      
      if (hasFilters) {
        // Apply profit filter
        if (this.filterCriteria.minProfit !== undefined) {
          filtered = filtered.filter(opp => 
            (opp.profit_ratio || 1.0) >= this.filterCriteria.minProfit!
          );
        }
        
        // Apply exchange filter
        if (this.filterCriteria.exchanges && this.filterCriteria.exchanges.length > 0) {
          filtered = filtered.filter(opp => 
            this.filterCriteria.exchanges!.includes(opp.buy_exchange) ||
            this.filterCriteria.exchanges!.includes(opp.sell_exchange)
          );
        }
        
        // Apply pairs filter
        if (this.filterCriteria.pairs && this.filterCriteria.pairs.length > 0) {
          filtered = filtered.filter(opp => 
            this.filterCriteria.pairs!.includes(opp.base)
          );
        }
      }
      
      // Always sort by profit ratio (highest first), then by time
      filtered.sort((a, b) => {
        const aProfitRatio = a.profit_ratio || 1.0;
        const bProfitRatio = b.profit_ratio || 1.0;
        const profitDiff = bProfitRatio - aProfitRatio;
        if (profitDiff !== 0) return profitDiff;
        return b.detected_at - a.detected_at;
      });
      
      this.filteredList = filtered;
      this.needsFilterUpdate = false;
      
      const elapsed = performance.now() - startTime;
      if (elapsed > 10) {
        console.warn('[Cache] Slow filter update:', elapsed.toFixed(2), 'ms for', this.opportunities.size, 'items');
      }
    } catch (error) {
      console.error('[Cache] Filter update error:', error);
      // Fallback to unfiltered list
      this.filteredList = Array.from(this.opportunities.values());
      this.needsFilterUpdate = false;
    }
  }
  
  // Get the pre-filtered list for UI display
  getFilteredList(): ArbitrageOpportunity[] {
    // Always update when called (every 75ms from ticker)
    // This is much simpler than tracking every single insert/update/delete
    this.updateFilteredList();
    return this.filteredList;
  }
}